import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";

import * as projectsApi from "./projects.api";
import { projectFormSchema } from "./projects.schema";
import { TProjectDetail, TProjectFormData, TProjectListItem, TUpdateProjectRequest } from "./projects.types";
import { IUseEditProject } from "./projects-edit.hooks.interface";

export const useEditProject = (
  clientId?: string,
  projectId?: string
): IUseEditProject => {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Buscar dados do projeto para preencher o formulário
  const { data: project, isLoading: isLoadingProject } = useQuery<TProjectDetail>({
    queryKey: ["project", projectId],
    queryFn: () => projectsApi.getProjectById(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  const projectForm = useForm<TProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: "",
      type_client: "affinity",
      level_hierarchical: undefined,
      user_commercials: [],
      partner_participants: [],
      hunters: [],
    },
  });

  // Buscar lista de projetos para obter o nome (já que o backend não retorna project_name no detalhe)
  const { data: projectsList = [] } = useQuery<TProjectListItem[]>({
    queryKey: ["projects", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      return projectsApi.getProjectsByOrganization(clientId);
    },
    enabled: !!clientId,
    staleTime: 60_000,
  });

  // Preencher formulário quando o projeto for carregado
  React.useEffect(() => {
    if (project) {
      // Buscar o nome do projeto da lista de projetos
      const projectFromList = projectsList.find(
        (p) => p.project_id === project.project_id
      );
      const projectName = projectFromList?.project_name || project.project_name || "";

      projectForm.reset({
        name: projectName,
        type_client: project.type_client || "affinity",
        level_hierarchical: project.level_hierarchical || undefined,
        user_commercials:
          project.user_commercials?.map((user) => ({
            id: user.id,
            commission_percentage: user.commission_percentage / 100, // Converter de centésimos para porcentagem
          })) || [],
        partner_participants:
          project.organization_participants?.map((participant) => ({
            id: participant.id,
          })) || [],
        hunters:
          project.hunter?.map((hunter) => ({
            id: hunter.id,
          })) || [],
      });
    }
  }, [project, projectsList, projectForm]);

  // Mutation para atualizar projeto
  const updateProjectMutation = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: TUpdateProjectRequest }) =>
      projectsApi.updateProject(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", clientId] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast({
        title: t("generic.success"),
        description: "Projeto atualizado com sucesso!",
      });
      if (clientId && projectId) {
        navigate(`/clients/${clientId}/projects/${projectId}`);
      }
    },
    onError: (error: any) => {
      // Tentar extrair mensagem de erro mais detalhada
      let errorMessage = "Erro ao atualizar projeto";

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      // Se for um array de mensagens, juntar todas
      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage.join(", ");
      }

      console.error("Erro ao atualizar projeto:", error);

      toast({
        title: t("generic.error"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = React.useCallback(
    (data: TProjectFormData) => {
      console.log("handleSubmit chamado com dados:", data);

      if (!projectId) {
        toast({
          title: t("generic.error"),
          description: "ID do projeto não encontrado",
          variant: "destructive",
        });
        return;
      }

      // Preparar dados para a API com a nova estrutura
      const requestData: TUpdateProjectRequest = {};

      // Adicionar campos básicos apenas se foram alterados
      if (data.name) {
        requestData.project_name = data.name;
      }

      // Não permitir atualizar o tipo do projeto
      // Removido: type_client não deve ser atualizado

      // Adicionar hierarchical_level apenas se o projeto for affiliate
      // Usar o tipo do projeto atual (não do formulário, já que não pode ser alterado)
      if (project?.type_client === "affiliate") {
        const hierarchicalLevel = data.level_hierarchical;
        if (hierarchicalLevel !== undefined && hierarchicalLevel !== null && !isNaN(Number(hierarchicalLevel))) {
          const levelInt = Math.floor(Number(hierarchicalLevel));
          requestData.hierarchical_level = levelInt > 0 ? levelInt : 1;
        }
      }

      // Adicionar usuários comerciais se existirem
      // Multiplicar por 100 para enviar ao backend (ex: 10.5% vira 1050)
      if (data.user_commercials && data.user_commercials.length > 0) {
        requestData.user_commercials = data.user_commercials.map((user) => ({
          id: user.id,
          commission_percentage: Math.round(user.commission_percentage * 100),
        }));
      } else {
        // Se o array estiver vazio, enviar array vazio para remover todos
        requestData.user_commercials = [];
      }

      // Adicionar parceiros participantes se existirem
      if (data.partner_participants && data.partner_participants.length > 0) {
        requestData.partner_particpants = data.partner_participants.map((partner) => ({
          id: partner.id,
        }));
      } else {
        // Se o array estiver vazio, enviar array vazio para remover todos
        requestData.partner_particpants = [];
      }

      // Adicionar hunters se existirem
      if (data.hunters && data.hunters.length > 0) {
        requestData.hunters = data.hunters.map((hunter) => ({
          id: hunter.id,
        }));
      } else {
        // Se o array estiver vazio, enviar array vazio para remover todos
        requestData.hunters = [];
      }

      console.log("Dados enviados para a API:", requestData);

      // Atualizar projeto
      updateProjectMutation.mutate({
        projectId,
        data: requestData,
      });
    },
    [projectId, project, updateProjectMutation, toast, t]
  );

  return {
    projectForm,
    handleSubmit,
    isUpdating: updateProjectMutation.isPending,
    isLoadingProject,
    project,
  };
};
